import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Package, Plus, Pencil, Trash2, X, AlertTriangle, ChevronRight, ChevronDown, Fingerprint, ArrowRightLeft, Wrench, PackageCheck, Truck, Tag, Boxes } from "lucide-react";
import Header from "@/components/Header";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { can } from "@/lib/perms";

export const ASSET_THRESHOLD = 50; // ab diesem Warenwert AssetID vorgeschlagen

const CATEGORIES = [
  { id: "tablet", de: "Tablet", en: "Tablet", asset: true },
  { id: "scanner_regula", de: "Scanner Regula", en: "Scanner Regula", asset: true },
  { id: "scanner_desko", de: "Scanner DESKO", en: "Scanner DESKO", asset: true },
  { id: "zubehoer", de: "Zubehör", en: "Accessory", asset: false },
  { id: "ersatzteil", de: "Ersatzteil", en: "Spare part", asset: false },
  { id: "sonstiges", de: "Sonstiges", en: "Other", asset: false },
];
const catMeta = (id) => CATEGORIES.find((c) => c.id === id) || CATEGORIES[5];
const STAGES = [
  { id: "eingang", de: "Wareneingang", en: "Received", cls: "text-amber-700 bg-amber-500/10 border-amber-500/30" },
  { id: "aufbereitung", de: "In Aufbereitung", en: "Refurbishing", cls: "text-sky-700 bg-sky-500/10 border-sky-500/30" },
  { id: "bereit", de: "Einsatzbereit", en: "Ready", cls: "text-[#c00000] bg-[#c00000]/10 border-[#c00000]/30" },
];
const stageMeta = (id) => STAGES.find((s) => s.id === id) || STAGES[2];
const scannerCat = (t) => { const s = String(t || "").toUpperCase(); if (s.includes("REGULA")) return "scanner_regula"; if (["DESKO", "PENTA", "1AC2"].some((k) => s.includes(k))) return "scanner_desko"; return null; };

export function ProductsPanel() {
  const { lang } = useLang();
  const de = lang === "de";
  const { user } = useAuth();
  const confirm = useConfirm();
  const canEdit = can(user, "lager", "edit");
  const canDelete = can(user, "lager", "delete");
  const [products, setProducts] = useState([]);
  const [devices, setDevices] = useState([]);
  const [scanners, setScanners] = useState([]);
  const [stationCodes, setStationCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", sku: "", unit: "Stk", stock: 0, reorder_level: 5, category: "sonstiges", unit_value: 0, track_assets: false });
  const [trackTouched, setTrackTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, inv, scn, dev] = await Promise.all([api.get("/products"), api.get("/inventory"), api.get("/scanners"), api.get("/devices")]);
      setProducts(pr.data || []);
      setDevices((inv.data || []).filter((d) => d.assignment === "lager"));
      setScanners((scn.data || []).filter((s) => s.status === "lager"));
      setStationCodes([...new Set((dev.data || []).map((x) => x.device_code).filter(Boolean))].sort());
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const unitsByCat = useMemo(() => {
    const m = { tablet: [], scanner_regula: [], scanner_desko: [] };
    devices.forEach((d) => m.tablet.push({ kind: "device", ...d }));
    scanners.forEach((s) => { const c = scannerCat(s.scanner_type); if (c) m[c].push({ kind: "scanner", ...s }); });
    return m;
  }, [devices, scanners]);

  const toggle = (id) => setExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const openNew = () => { setEditing(null); setTrackTouched(false); setForm({ name: "", sku: "", unit: "Stk", stock: 0, reorder_level: 5, category: "sonstiges", unit_value: 0, track_assets: false }); setDlg(true); };
  const openEdit = (p) => { setEditing(p); setTrackTouched(true); setForm({ name: p.name, sku: p.sku || "", unit: p.unit || "Stk", stock: p.stock || 0, reorder_level: p.reorder_level || 0, category: p.category || "sonstiges", unit_value: p.unit_value || 0, track_assets: !!p.track_assets }); setDlg(true); };

  const onValueChange = (v) => setForm((f) => ({ ...f, unit_value: v, track_assets: trackTouched ? f.track_assets : Number(v) >= ASSET_THRESHOLD }));
  const onCategoryChange = (c) => setForm((f) => ({ ...f, category: c, track_assets: trackTouched ? f.track_assets : !!catMeta(c).asset }));

  const save = async () => {
    if (!form.name.trim()) { toast.error(de ? "Name erforderlich" : "Name required"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, sku: form.sku, unit: form.unit, stock: Number(form.stock), reorder_level: Number(form.reorder_level), category: form.category, unit_value: Number(form.unit_value), track_assets: form.track_assets };
      if (editing) await api.patch(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(de ? "Gespeichert" : "Saved"); setDlg(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const del = async (p) => {
    const ok = await confirm({ title: de ? "Produkt löschen?" : "Delete product?", message: p.name, confirmLabel: de ? "Löschen" : "Delete", danger: true });
    if (!ok) return;
    try { await api.delete(`/products/${p.id}`); load(); } catch (err) { toast.error(apiError(err)); }
  };
  const adjust = async (p, delta) => {
    try { await api.patch(`/products/${p.id}`, { stock: Math.max(0, (p.stock || 0) + delta) }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const setStage = async (u, stage) => {
    setBusy(u.asset_tag);
    try { await api.post(`/${u.kind === "scanner" ? "scanners" : "devices"}/${encodeURIComponent(u.asset_tag)}/stage`, { stage }); await load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };
  const markDefect = async (u) => {
    if (!(await confirm({ title: de ? "Als defekt einstufen" : "Mark defective", message: u.asset_tag, confirmLabel: de ? "Als defekt" : "Mark", danger: true }))) return;
    setBusy(u.asset_tag);
    try { await api.post(`/${u.kind === "scanner" ? "scanners" : "devices"}/${encodeURIComponent(u.asset_tag)}/condition`, { condition: "defekt" }); await load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };
  const assign = async (u) => {
    const target = (window.prompt(de ? "Zielstation (Geräte-Code), z. B. FRAT03-01:" : "Target station code:", "") || "").trim().toUpperCase();
    if (!target) return;
    setBusy(u.asset_tag);
    try { await api.post(`/${u.kind === "scanner" ? "scanners" : "devices"}/${encodeURIComponent(u.asset_tag)}/assign`, { device_code: target }); toast.success(`${u.asset_tag} → ${target}`); await load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const low = products.filter((p) => (p.available ?? p.stock ?? 0) <= (p.reorder_level || 0));
  const inputCls = "w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#c00000]/50";
  const labelCls = "block text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1.5";

  return (
    <>
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <p className="text-sm text-gray-600">{de ? "Katalog mit Verfügbarkeit. Einsatzbereite Lagergeräte zählen automatisch als bestellbar." : "Catalog with availability. Ready stock devices count as orderable."}</p>
        {canEdit && <button data-testid="product-new-btn" onClick={openNew} className="flex items-center gap-2 bg-[#c00000]/90 hover:bg-[#c00000] text-black font-semibold rounded-lg px-3 py-2 text-sm transition-colors"><Plus className="w-4 h-4" /> {de ? "Neues Produkt" : "New product"}</button>}
      </div>

      {low.length > 0 && (
        <div data-testid="low-stock-banner" className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-700 rounded-xl px-4 py-3 my-4 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0" /> {de ? `Nachbestellen: ${low.length} Produkt(e) am/unter Mindestbestand` : `Reorder: ${low.length} at/below level`} — {low.map((p) => p.name).join(", ")}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-600 mt-4"><Loader2 className="w-4 h-4 animate-spin" /> …</div>
      ) : (
        <div className="space-y-2 mt-4" data-testid="products-list">
          {products.map((p) => {
            const cm = catMeta(p.category);
            const avail = p.available ?? p.stock ?? 0;
            const isLow = avail <= (p.reorder_level || 0);
            const isAsset = !!p.track_assets;
            const units = unitsByCat[p.category] || [];
            const pipe = p.pipeline || { eingang: 0, aufbereitung: 0, bereit: 0 };
            const isOpen = expanded.has(p.id);
            return (
              <div key={p.id} data-testid={`product-row-${p.id}`} className="bg-gray-50 border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between gap-3 p-3.5">
                  <div className="min-w-0 flex items-center gap-3">
                    {isAsset && (
                      <button data-testid={`product-expand-${p.id}`} onClick={() => toggle(p.id)} className="text-gray-600 hover:text-gray-900 shrink-0">
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900 truncate">{p.name}</span>
                        <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-600">{de ? cm.de : cm.en}</span>
                        {isAsset ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-slate-200 bg-slate-100 text-slate-700"><Fingerprint className="w-3 h-3" /> AssetID</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-600"><Boxes className="w-3 h-3" /> {de ? "Menge" : "Bulk"}</span>
                        )}
                        {p.sku && <span className="text-xs text-gray-600 font-mono">· {p.sku}</span>}
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{de ? "Mindestbestand" : "Reorder"}: {p.reorder_level} {p.unit}</span>
                        {p.unit_value > 0 && <span>· {de ? "Wert" : "Value"}: {Number(p.unit_value).toLocaleString(de ? "de-DE" : "en-US", { style: "currency", currency: "EUR" })}</span>}
                        {isAsset && (
                          <>
                            {pipe.eingang > 0 && <span data-testid={`product-pipe-eingang-${p.id}`} className="text-amber-700">· {pipe.eingang} {de ? "Wareneingang" : "received"}</span>}
                            {pipe.aufbereitung > 0 && <span data-testid={`product-pipe-aufbereitung-${p.id}`} className="text-sky-700">· {pipe.aufbereitung} {de ? "in Aufbereitung" : "refurbishing"}</span>}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {!isAsset && canEdit && <button data-testid={`product-dec-${p.id}`} onClick={() => adjust(p, -1)} className="w-7 h-7 rounded bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100">−</button>}
                    <div className="min-w-[4rem] text-center">
                      <span data-testid={`product-available-${p.id}`} className={`font-mono text-xl font-bold ${isLow ? "text-amber-700" : "text-[#c00000]"}`}>{avail}</span>
                      <div className="text-[10px] font-mono text-gray-600 leading-tight">{de ? "verfügbar" : "available"}</div>
                      {isAsset && p.reusable > 0 && <div data-testid={`product-reusable-${p.id}`} className="text-[10px] font-mono text-[#c00000]/80 leading-tight">{p.reusable} {de ? "bereit" : "ready"}</div>}
                    </div>
                    {!isAsset && canEdit && <button data-testid={`product-inc-${p.id}`} onClick={() => adjust(p, +1)} className="w-7 h-7 rounded bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100">+</button>}
                    {isLow && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border text-amber-700 bg-amber-500/15 border-amber-500/30">{de ? "nachbestellen" : "reorder"}</span>}
                    {canEdit && (
                      <div className="flex items-center gap-1">
                        <button data-testid={`product-edit-${p.id}`} onClick={() => openEdit(p)} className="text-gray-600 hover:text-gray-900 p-1.5"><Pencil className="w-3.5 h-3.5" /></button>
                        {canDelete && <button data-testid={`product-delete-${p.id}`} onClick={() => del(p)} className="text-gray-600 hover:text-red-700 p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>}
                      </div>
                    )}
                  </div>
                </div>

                {isAsset && isOpen && (
                  <div className="border-t border-gray-200 p-3 space-y-1.5" data-testid={`product-units-${p.id}`}>
                    {units.length === 0 ? (
                      <div className="text-xs text-gray-600 py-2 text-center">{de ? "Keine Einzelstücke im Lager." : "No units in stock."}</div>
                    ) : units.map((u) => {
                      const st = stageMeta(u.lager_stage || "bereit");
                      return (
                        <div key={u.asset_tag} data-testid={`unit-row-${u.asset_tag}`} className="flex items-center justify-between gap-2 bg-white/[0.02] border border-gray-100 rounded-lg px-2.5 py-2">
                          <div className="min-w-0 flex items-center gap-2">
                            <span className="font-mono text-sm text-gray-900">{u.asset_tag}</span>
                            <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${st.cls}`}>{de ? st.de : st.en}</span>
                            {u.origin === "schliessung" && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-600">{de ? "aus Schließung" : "from closure"}</span>}
                            {u.kind === "scanner" && u.scanner_serial && <span className="text-[11px] font-mono text-gray-600">{u.scanner_serial}</span>}
                          </div>
                          {canEdit && (
                            <div className="flex items-center gap-1 shrink-0">
                              {STAGES.map((s) => (
                                <button key={s.id} data-testid={`unit-stage-${s.id}-${u.asset_tag}`} disabled={busy === u.asset_tag || (u.lager_stage || "bereit") === s.id}
                                  onClick={() => setStage(u, s.id)}
                                  title={de ? s.de : s.en}
                                  className={`text-[10px] px-1.5 py-1 rounded border transition-colors disabled:opacity-40 ${(u.lager_stage || "bereit") === s.id ? s.cls : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"}`}>
                                  {s.id === "eingang" ? <PackageCheck className="w-3 h-3" /> : s.id === "aufbereitung" ? <Wrench className="w-3 h-3" /> : <Tag className="w-3 h-3" />}
                                </button>
                              ))}
                              <button data-testid={`unit-assign-${u.asset_tag}`} disabled={busy === u.asset_tag} onClick={() => assign(u)} className="flex items-center gap-1 text-[11px] bg-[#c00000]/15 border border-[#c00000]/30 text-[#c00000] rounded px-2 py-1 hover:bg-[#c00000]/25 disabled:opacity-40"><ArrowRightLeft className="w-3 h-3" /> {de ? "Zuweisen" : "Assign"}</button>
                              <button data-testid={`unit-defect-${u.asset_tag}`} disabled={busy === u.asset_tag} onClick={() => markDefect(u)} className="text-[11px] bg-gray-50 border border-gray-200 text-gray-600 rounded px-2 py-1 hover:text-red-700 disabled:opacity-40">{de ? "Defekt" : "Defect"}</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {dlg && (
        <>
          <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm" onClick={() => setDlg(false)} />
          <div data-testid="product-dialog" className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-[calc(100vw-2rem)] max-w-md max-h-[90vh] overflow-y-auto bg-gray-50 border border-gray-200 rounded-2xl shadow-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">{editing ? (de ? "Produkt bearbeiten" : "Edit product") : (de ? "Neues Produkt" : "New product")}</h3>
              <button onClick={() => setDlg(false)} className="text-gray-600 hover:text-gray-900"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div><label className={labelCls}>{de ? "Name" : "Name"}</label><input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>{de ? "Kategorie" : "Category"}</label>
                  <select data-testid="product-category-select" value={form.category} onChange={(e) => onCategoryChange(e.target.value)} className={inputCls}>
                    {CATEGORIES.map((c) => <option key={c.id} value={c.id} className="bg-gray-50">{de ? c.de : c.en}</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>SKU/Art.-Nr.</label><input data-testid="product-sku-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className={inputCls} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className={labelCls}>{de ? "Bestand" : "Stock"}</label><input data-testid="product-stock-input" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={inputCls} disabled={form.track_assets} title={form.track_assets ? (de ? "AssetID-Artikel: Bestand kommt aus Einzelstücken" : "Asset item: stock from units") : ""} /></div>
                <div><label className={labelCls}>{de ? "Mindest." : "Reorder"}</label><input data-testid="product-reorder-input" type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} className={inputCls} /></div>
                <div><label className={labelCls}>{de ? "Wert €" : "Value €"}</label><input data-testid="product-value-input" type="number" value={form.unit_value} onChange={(e) => onValueChange(e.target.value)} className={inputCls} /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer bg-white/[0.03] border border-gray-200 rounded-lg px-3 py-2.5">
                <input data-testid="product-track-toggle" type="checkbox" checked={form.track_assets} onChange={(e) => { setTrackTouched(true); setForm({ ...form, track_assets: e.target.checked }); }} className="accent-slate-500 w-4 h-4" />
                <Fingerprint className="w-4 h-4 text-slate-600" />
                <span>{de ? "AssetID-pflichtig (Einzelstück-Verfolgung)" : "Requires AssetID (per-unit tracking)"}</span>
              </label>
              {!trackTouched && Number(form.unit_value) >= ASSET_THRESHOLD && !form.track_assets && (
                <p className="text-[11px] text-amber-700/80">{de ? `Vorschlag: Ab ${ASSET_THRESHOLD} € empfiehlt sich AssetID-Verfolgung.` : `Suggestion: track with AssetID above €${ASSET_THRESHOLD}.`}</p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={() => setDlg(false)} className="px-4 py-2 rounded-lg text-sm text-gray-700 border border-gray-200 hover:bg-gray-50">{de ? "Abbrechen" : "Cancel"}</button>
              <button data-testid="product-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#c00000]/90 hover:bg-[#c00000] text-black disabled:opacity-50">{saving && <Loader2 className="w-4 h-4 animate-spin" />} {de ? "Speichern" : "Save"}</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default function Products() {
  return (
    <div className="min-h-screen bg-white text-gray-800">
      <Header />
      <div className="w-full px-4 sm:px-6 py-6"><ProductsPanel /></div>
    </div>
  );
}
